import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { INotificationsService, INOTIFICATIONS_SERVICE } from '@platform500services/p500-ui-kit';
import { Observable, throwError } from 'rxjs';
import { catchError, debounceTime, take } from 'rxjs/operators';
import { AuthServiceInterface } from '../services/auth/auth-service.interface';
import { ampli } from 'src/ampli';
import { IEnvironmentService } from '../services/environment/environment.interface';

@Injectable()
class JwtInterceptor implements HttpInterceptor {
  constructor(
    @Inject(INOTIFICATIONS_SERVICE)
    private notificationsService: INotificationsService,
    private router: Router,
    @Inject('AuthServiceInterface') private authService: AuthServiceInterface,
    private route: ActivatedRoute,
    @Inject('IEnvironmentService') private envService: IEnvironmentService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = localStorage.getItem('tracking-token');
    const isNotUsingInterceptor = request.context.get(TURN_OFF_INTERCEPTOR) === true;
    const authRequest =
      token && !isNotUsingInterceptor
        ? request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          })
        : request;

    if (this.envService.getVar('production')) {
      this.sendAmpliEvent(authRequest);
    }

    return next.handle(authRequest).pipe(
      catchError((err) => {
        if (authRequest.url.includes('getComparisonFields') || authRequest.url.includes('getConversionTypes')) {
          return throwError(err);
        }
        if ('redirectTo' in err.error && err.status === 401) {
          window.location.href = err.error.redirectTo;
        }
        this.showErrorsMsgs(err.error);
        if (err.status === 401) {
          const redirectPath = location.pathname.startsWith('/app')
            ? location.pathname
            : '/' + location.pathname.split('/').slice(2).join('/');
          this.route.queryParams.pipe(take(2), debounceTime(300)).subscribe((params) => {
            this.authService.logout();
            const queryParams: { redirectPath: string; logout?: boolean } = {
              redirectPath,
            };
            if (params['logout']) {
              queryParams.logout = true;
            }
            this.router.navigate(['/auth/signin'], { queryParams });
          });
        }
        return throwError(err);
      })
    );
  }

  private showErrorsMsgs(data: any) {
    switch (Object.keys(data)[0]) {
      case 'validation_errors':
        for (const value of Object.values(data.validation_errors)) {
          if (!Array.isArray(value)) {
            break;
          }
          value.forEach((error: string) => this.addMsg(error));
        }
        break;
      case 'error': {
        const msg = typeof data.error === 'string' ? data.error : data.error.message;
        if (data.error?.otp) {
          break;
        }
        this.addMsg(msg);
        break;
      }
      case 'message': {
        let type: 'error' | 'success' = 'error';
        if (data.message === 'The confirmation code was sent.') {
          type = 'success';
        }
        this.addMsg(data.message, type);
        break;
      }
      default:
        this.addMsg();
        break;
    }
  }

  private addMsg(msg: string = '', type: 'error' | 'success' = 'error'): void {
    const defaultMsg = 'Something went wrong...';
    msg = msg?.length ? msg : defaultMsg;
    const isShortMsg = msg?.length < 72;
    this.notificationsService.add(
      {
        type,
        title: isShortMsg ? msg : defaultMsg,
        more: isShortMsg
          ? null
          : [
              {
                title: 'Log',
                message: JSON.stringify(msg),
              },
            ],
      },
      true
    );
  }

  private sendAmpliEvent(request: HttpRequest<any>) {
    if (request.body) {
      if (request.body['widget']) {
        ampli.getWidget({ name: request.body['widget'] });
      } else if (request.body['table']) {
        ampli.getTable({ name: request.body['table'] });
      } else if (request.body['action']) {
        ampli.executeAction({ name: request.body['action'] });
      } else if (request.url.includes('drill-down/data')) {
        ampli.generateDrilldown({ name: 'Generate Drilldown' });
      } else if (request.url.includes('reporting/generate')) {
        ampli.generateDataExporter({ name: 'Generate Data Exporter' });
      }
    }
  }
}
const TURN_OFF_INTERCEPTOR = new HttpContextToken(() => false);

export { JwtInterceptor, TURN_OFF_INTERCEPTOR };
